import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  styled,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import classes from "./IDMCredentialTracking.module.css";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import CreateUpdateIdmRecord from "../IdmCredentialTracking/CreateUpdateIdmRecord";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  getIdmCredentialByTrackingId,
  getIdmCredentialRosterDetailsByTrackingId,
  getIdmCredentialsByProductTrainingMonth,
  getIdmRosterList,
  getIdmSystemImpacted,
  getIdmSwitchType,
  getIdmSanityCheckNotes,
  getIdmIssueDescription,
  deleteIdmCredential,
} from "services/userServices";
import BackdropComponent from "components/Spinner/Spinner";
import { useNavigate } from "react-router-dom";
import { Delete, Edit } from "@mui/icons-material";
import SnackBar from "components/SnackBar/SnackBar";
import moment from "moment";
import { red } from "@mui/material/colors";
import { Transition } from "components/Common/Header/Header";
import { useAuth } from "context/AuthContext";

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const IDMCredentialTracking = () => {
  const { hasPermission } = useAuth();

  const [idmRecordOpen, setIdmRecordOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedIdmTrackingId, setSelectedIdmTrackingId] = useState(0);
  const [editAccess, setEditAccess] = useState(false);
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [idmRosterDetails, setIdmRosterDetails] = useState();
  const [idmTrackingDetails, setIdmTrackingDetails] = useState();
  const [systemImpacted, setSystemImpacted] = useState([]);
  const [rosterId, setRosterId] = useState([]);
  const [switchType, setSwitchType] = useState([]);
  const [sanityCheckNotes, setSanityCheckNotes] = useState([]);
  const [issueDescription, setIssueDescription] = useState([]);
  const [gridPageSize, setGridPageSize] = useState(10);
  const [selectedMonthYear, setSelectedMonthYear] = useState("");

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const showSnack = (message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const calculatePageSize = () => {
    const height = window.innerHeight;
    if (height > 1200) return 100;
    if (height > 900) return 50;
    if (height > 600) return 25;
    return 10;
  };

  const prepareRowData = (rowData) => {
    return rowData
      .map((item, index) => ({
        ...item,
        id: index + 1,
        ClassRosterId:
          item?.ClassRosterId === 0 ? "NA" : item?.ClassRosterId.toString(),
      }))
      .sort((a, b) => b.IDMCredentialTrackingId - a.IDMCredentialTrackingId);
  };

  const currentMonth = new Date().getMonth() + 1; // month is zero-indexed
  const currentYear = new Date().getFullYear();
  async function fetchIdmCredentialsByProductTrainingMonth(month, year) {
    try {
      setLoading(true);
      const response = await getIdmCredentialsByProductTrainingMonth(
        month,
        year,
      );

      if (response.statusCode === 200) {
        showSnack(response?.message, "success");

        const updatePageSize = () => {
          setGridPageSize(calculatePageSize());
        };

        updatePageSize();

        window.addEventListener("resize", updatePageSize);

        return response.data;
      } else {
        showSnack(response?.message, "error");
        return [];
      }
    } catch (e) {
      showSnack("Internal error occured.", "error");
    } finally {
      setLoading(false);
    }
  }

  const rosterSearchLink = (rowData) => {
    const hasRoster =
      rowData.ClassRosterId &&
      rowData.ClassRosterId !== "NA" &&
      rowData.ClassRosterId !== 0 &&
      rowData.ClassRosterId !== "0";

    return hasRoster ? (
      <Tooltip title={`View roster details for ${rowData.ClassRosterId}`} arrow>
        <Link
          sx={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              `/home/searchrosters?classRequestId=${rowData.ClassRequestId}&classRosterId=${rowData.ClassRosterId}`,
            )
          }
        >
          {rowData.ClassRosterId}
        </Link>
      </Tooltip>
    ) : (
      rowData.ClassRosterId || "NA"
    );
  };

  const setUpIdmCredentialModalSources = async (trackingId) => {
    setLoading(true);
    let idmDetailsFetched = false;
    try {
      const fetchData = async (data, setter, service) => {
        if (data.length === 0) {
          const response = await service();
          if (response?.message === "Success") {
            setter(response?.data);
          }
        }
      };

      await Promise.all([
        fetchData(rosterId, setRosterId, getIdmRosterList),
        fetchData(systemImpacted, setSystemImpacted, getIdmSystemImpacted),
        fetchData(switchType, setSwitchType, getIdmSwitchType),
        fetchData(
          sanityCheckNotes,
          setSanityCheckNotes,
          getIdmSanityCheckNotes,
        ),
        fetchData(
          issueDescription,
          setIssueDescription,
          getIdmIssueDescription,
        ),
      ]);

      if (trackingId > 0) {
        const [rosterDetailsResponse, idmCredentialDetailsResponse] =
          await Promise.all([
            getIdmCredentialRosterDetailsByTrackingId(trackingId),
            getIdmCredentialByTrackingId(trackingId),
          ]);

        if (rosterDetailsResponse.statusCode === 200) {
          setIdmRosterDetails(rosterDetailsResponse.data[0]);
          idmDetailsFetched = true;
        } else {
          // Don't silently keep whatever idmRosterDetails happened to be set
          // to before (previously this fell through and left stale/empty
          // state, which the modal would then misinterpret as a valid "no
          // class roster" record). Surface the failure and refuse to open
          // the modal instead of showing an incomplete/broken record.
          showSnack(
            "Unable to load roster details for this record. The assigned project may be inactive or invalid.",
            "error",
          );
        }
        if (idmCredentialDetailsResponse.statusCode === 200) {
          setIdmTrackingDetails(idmCredentialDetailsResponse.data[0]);
        }
      }
    } catch (error) {
      showSnack("Internal error occurred.", "error");
    } finally {
      setLoading(false);
    }
    return trackingId === 0 || (trackingId > 0 && idmDetailsFetched);
  };

  const handleEditClick = async (params) => {
    setLoading(true);
    setSelectedIdmTrackingId(params.IDMCredentialTrackingId);
    const response = await setUpIdmCredentialModalSources(
      params.IDMCredentialTrackingId,
    );
    if (response) {
      setIdmRecordOpen(true);
    }
    setLoading(false);
  };

  const handleDeleteClick = async (params) => {
    setSelectedIdmTrackingId(params.IDMCredentialTrackingId);
    setIsDeleteDialogOpen(true);
  };

  const editTemplate = (params) => {
    return (
      <Tooltip title="Edit Record" arrow>
        <IconButton onClick={() => handleEditClick(params)}>
          <Edit sx={{ color: "#3299FF" }} />
        </IconButton>
      </Tooltip>
    );
  };

  const deleteTemplate = (params) => {
    return (
      <Tooltip title="Delete Record" arrow>
        <IconButton onClick={() => handleDeleteClick(params)}>
          <Delete sx={{ color: red[400] }} />
        </IconButton>
      </Tooltip>
    );
  };

  const handleDeleteIdmCredential = async () => {
    setLoading(true);
    const response = await deleteIdmCredential(selectedIdmTrackingId);
    setLoading(false);
    if (response.message === "Success") {
      showSnack("Idm credential record deleted successfully", "success");

      fetchIdmCredentialTrackingDetails();
    } else {
      showSnack("Some internal error occured.", "error");
    }
    setIsDeleteDialogOpen(false);
  };

  const TooltipCell = ({ value, width = "120px", textAlign = "left" }) => (
    <Tooltip title={value || "N/A"} arrow placement="top">
      <div
        style={{
          width: width,
          minWidth: width,
          maxWidth: width,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: textAlign,
        }}
      >
        {value}
      </div>
    </Tooltip>
  );

  // <Column rowEditor={allowEdit} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
  const columns = [
    ...(editAccess
      ? [
          {
            field: "id",
            header: "Edit",
            align: "center",
            bodyStyle: { textAlign: "center" },
            body: (params) => editTemplate(params),
          },
          {
            field: "id",
            header: "Delete",
            align: "center",
            bodyStyle: { textAlign: "center" },
            body: (params) => deleteTemplate(params),
          },
        ]
      : []),
    {
      field: "IDMCredentialTrackingId",
      header: "Tracking Id",
      filter: false,
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "right" },
      body: (rowData) => (
        <TooltipCell
          value={rowData.IDMCredentialTrackingId}
          width="100px"
          textAlign="right"
        />
      ),
    },
    {
      field: "ClassRosterId",
      header: "Roster Id",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Class Roster ID",
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <div style={{ width: "120px", minWidth: "120px", textAlign: "center" }}>
          {rosterSearchLink(params)}
        </div>
      ),
    },
    {
      field: "AskNowTicketNumber",
      header: "RITM Number",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by RITM Ticket Number",
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (rowData) => (
        <TooltipCell
          value={rowData.AskNowTicketNumber}
          width="150px"
          textAlign="center"
        />
      ),
    },
    {
      field: "SiteName",
      header: "Site",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Site",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell value={rowData.SiteName} width="220px" textAlign="left" />
      ),
    },
    {
      field: "Client",
      header: "Client",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Client",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell value={rowData.Client} width="220px" textAlign="left" />
      ),
    },
    {
      field: "Project",
      header: "Project",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Project",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell value={rowData.Project} width="220px" textAlign="left" />
      ),
    },
    {
      field: "AskNowTDT",
      header: "Technology Deployment Type",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Technology Deployment Type",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell value={rowData.AskNowTDT} width="220px" textAlign="left" />
      ),
    },
    {
      field: "RosterLockDate",
      header: "Roster Locked Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Roster Locked Date",
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (rowData) => {
        const formattedDate = rowData.RosterLockDate
          ? moment(rowData.RosterLockDate).format("MM/DD/YYYY")
          : "";
        return (
          <Tooltip title={formattedDate || "No date"} arrow placement="top">
            <div
              style={{
                width: "220px",
                minWidth: "220px",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formattedDate}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "ProductTrainingDate",
      header: "Training Start Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Training Start Date",
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (rowData) => {
        const formattedDate = rowData.ProductTrainingDate
          ? moment(rowData.ProductTrainingDate).format("MM/DD/YYYY")
          : "";
        return (
          <Tooltip title={formattedDate || "No date"} arrow placement="top">
            <div
              style={{
                width: "220px",
                minWidth: "220px",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formattedDate}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "SubmittedBy",
      header: "Submitted By",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Submitted by (user)",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell
          value={rowData.SubmittedBy}
          width="220px"
          textAlign="left"
        />
      ),
    },
    {
      field: "DateCheckedByTL",
      header: "Date Checked By",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Date Checked by TL",
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (rowData) => {
        const formattedDate = rowData.DateCheckedByTL
          ? moment(rowData.DateCheckedByTL).format("MM/DD/YYYY HH:mm")
          : "";
        return (
          <Tooltip title={formattedDate || "No date"} arrow placement="top">
            <div
              style={{
                width: "220px",
                minWidth: "220px",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formattedDate}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "IDMTeamLead",
      header: "IDM Team Lead",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by IDM Team Lead",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell
          value={rowData.IDMTeamLead}
          width="220px"
          textAlign="left"
        />
      ),
    },
    {
      field: "PrimaryIDMPOC",
      header: "Primary IDM POC",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Primary IDM POC",
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <TooltipCell
          value={rowData.PrimaryIDMPOC}
          width="220px"
          textAlign="left"
        />
      ),
    },
  ];
  const [rows, setRows] = useState([]);

  const filterProps = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ClassRosterId: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    AskNowTicketNumber: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    SiteName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    Client: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    Project: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    AskNowTDT: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    RosterLockDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    ProductTrainingDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    SubmittedBy: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    DateCheckedByTL: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    IDMTeamLead: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    PrimaryIDMPOC: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const rowData = await fetchIdmCredentialsByProductTrainingMonth(
          currentMonth,
          currentYear,
        );

        if (cancelled) return;

        if (!cancelled) {
          setRows(prepareRowData(rowData));
          setSelectedMonthYear(moment(selectedMonth?.$d).format("MMMM, YYYY"));

          if (hasPermission("WFM_IDM_Group_Admin")) {
            setEditAccess(true);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch IDM credentials:", error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const AddIdmRecord = async () => {
    const response = await setUpIdmCredentialModalSources(0);
    if (response) {
      setIdmRecordOpen(true);
    }
  };

  const fetchIdmCredentialTrackingDetails = async () => {
    const month = selectedMonth ? selectedMonth.month() + 1 : currentMonth; // month is zero-indexed
    const year = selectedMonth ? selectedMonth.year() : currentYear;
    const rowData = await fetchIdmCredentialsByProductTrainingMonth(
      month,
      year,
    );
    if (rowData.length > 0) setRows(prepareRowData(rowData));
    else setRows([]);
  };

  const handleClose = async (shouldRefresh) => {
    if (shouldRefresh) {
      fetchIdmCredentialTrackingDetails();
    }
    setIdmRosterDetails({});
    setIdmTrackingDetails({});
    setSelectedIdmTrackingId(0);
    setIdmRecordOpen(false);
  };

  const handleSearch = async () => {
    fetchIdmCredentialTrackingDetails();
  };

  const handleChange = (newValue) => {
    setSelectedMonthYear(moment(newValue?.$d).format("MMMM, YYYY"));
    setSelectedMonth(newValue);
  };
  return (
    <>
      {loading ? <BackdropComponent /> : null}
      {idmRecordOpen && (
        <CreateUpdateIdmRecord
          idmRosterDetails={idmRosterDetails}
          idmTrackingDetails={idmTrackingDetails}
          idmRecordOpen={idmRecordOpen}
          handleClose={handleClose}
          trackingId={selectedIdmTrackingId}
          systemImpacted={systemImpacted}
          rosterId={rosterId}
          switchType={switchType}
          sanityCheckNotes={sanityCheckNotes}
          issueDescription={issueDescription}
          isEdit={editAccess}
        />
      )}

      <Paper
        sx={{
          flexDirection: "column",
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
          padding: 3,
          overflow: "auto",
          height: "calc(100vh - 80px)",
          width: "calc(100vw - 125px)",
        }}
      >
        <Grid container spacing={2} item xs={12}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontSize: 18,
                fontWeight: "bold",
                color: "#3F5765",
                letterSpacing: "0.10em",
              }}
            >
              IDM Credential Tracking
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Tooltip title="It is TTF Date but may fail-over to Product Training Date">
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  fontWeight: "bold",
                  color: "#3F5765",
                  letterSpacing: "0.10em",
                  fontSize: 12,
                  marginTop: 0.75,
                  marginBottom: 0.75,
                }}
              >
                Training Start date
              </Typography>
            </Tooltip>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={selectedMonth || new Date()}
                  views={["month", "year"]}
                  onChange={(newValue) => {
                    handleChange(newValue);
                  }}
                  renderInput={(params) => (
                    <Tooltip title="It is TTF Date but may fail-over to Product Training Date">
                      <TextField size="small" {...params} />
                    </Tooltip>
                  )}
                />
              </LocalizationProvider>

              <Tooltip title="Search for IDM credentials by month" arrow>
                <CustomButton variant="contained" onClick={handleSearch}>
                  Search
                </CustomButton>
              </Tooltip>
              <Tooltip title="Add new IDM credential record" arrow>
                <span>
                  <CustomOutlinedButton
                    variant="outlined"
                    onClick={AddIdmRecord}
                    disabled={!editAccess}
                  >
                    Add
                  </CustomOutlinedButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} className={classes.primeReactGridContainer}>
            <PrimeReactDataGrid
              enableScrollable={true}
              enableStickyHeader={true}
              scrollHeight="calc(100vh - 370px)"
              dataKey="id"
              customtitle={selectedMonthYear}
              size="small"
              removableSort
              showGridlines
              resizableColumns
              paginator
              rows={gridPageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
              columnResizeMode="expand"
              selectionMode="single"
              dragSelection
              metaKeySelection={false}
              value={rows}
              columns={columns}
              filterprops={filterProps}
              globalFilterFields={[
                "IDMCredentialTrackingId",
                "ClassRosterId",
                "AskNowTicketNumber",
                "SiteName",
                "Client",
                "Project",
                "AskNowTDT",
                "RosterLockDate",
                "ProductTrainingDate",
                "SubmittedBy",
                "DateCheckedByTL",
                "IDMTeamLead",
                "PrimaryIDMPOC",
              ]}
              sortMode="multiple"
              emptyMessage="No records found."
            ></PrimeReactDataGrid>
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        TransitionComponent={Transition}
      >
        {loading ? <BackdropComponent /> : null}
        <DialogTitle>{"Delete IDM Credential"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container>
              <Grid item xs={12}>
                {" "}
                <b>Tracking Id: {selectedIdmTrackingId}</b>
              </Grid>
              <Grid item xs={12}>
                {" "}
                Are you sure you want to delete ?
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton variant="contained" onClick={handleDeleteIdmCredential}>
            Delete
          </CustomButton>

          <CustomOutlinedButton
            variant="outlined"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </CustomOutlinedButton>
        </DialogActions>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
};

export default IDMCredentialTracking;
